import { graphql } from "gatsby";
import React from "react";
import { fallbackGetMicroCopy, GetMicroCopy } from "./MicroCopy";
import type { FooterMainNavigation } from "./Footer";
import type { ExternalLinkBlock, InternalLinkBlock } from "./link/types";
import type { NavigationRootMenu, Region } from "./Header";
import type { PayloadHomePage, PayloadSite } from "../types/payload";

export type HomePageData = Pick<PayloadHomePage, "title">;

export type Context = {
  node_locale: string;
  countryCode: string;
  homePage: HomePageData;
  getMicroCopy: GetMicroCopy;
  reCaptchaKey?: string;
  reCaptchaNet?: boolean;
  pageId?: string;
  payloadPageId?: string;
};

const SiteContext = React.createContext<Context>({
  node_locale: "",
  countryCode: "",
  homePage: {
    title: ""
  },
  getMicroCopy: fallbackGetMicroCopy
});

export const SiteContextProvider = SiteContext.Provider;

export const useSiteContext = () => React.useContext(SiteContext);

export type SiteData = Pick<PayloadSite, "countryCode"> & {
  menuNavigation: (
    | InternalLinkBlock
    | ExternalLinkBlock
    | NavigationRootMenu
  )[];
  menuNavigationSiteMapTitle: string;
  menuUtilities: (InternalLinkBlock | ExternalLinkBlock)[] | null;
  menuUtilitiesSiteMapTitle: string | null;
  footerMainNavigation: FooterMainNavigation[] | null;
  footerMainNavigationSiteMapTitle: string | null;
  footerSecondaryNavigation: (InternalLinkBlock | ExternalLinkBlock)[] | null;
  footerSecondaryNavigationSiteMapTitle: string | null;
  regions: Region[] | null;
};

export const query = graphql`
  fragment SiteFragment on PayloadSite {
    countryCode
    menuNavigation {
      ... on PayloadNavigationRootMenu {
        ...HeaderNavigationRootMenuFragment
      }
      ... on PayloadInternalLinkBlock {
        ...InternalLinkBlockFragment
      }
      ... on PayloadExternalLinkBlock {
        ...ExternalLinkBlockFragment
      }
    }
    menuNavigationSiteMapTitle
    menuUtilities {
      ... on PayloadInternalLinkBlock {
        ...InternalLinkBlockFragment
      }
      ... on PayloadExternalLinkBlock {
        ...ExternalLinkBlockFragment
      }
    }
    menuUtilitiesSiteMapTitle
    footerMainNavigation {
      ...FooterMainNavigationFragment
    }
    footerMainNavigationSiteMapTitle
    footerSecondaryNavigation {
      ... on PayloadInternalLinkBlock {
        ...InternalLinkBlockFragment
      }
      ... on PayloadExternalLinkBlock {
        ...ExternalLinkBlockFragment
      }
    }
    footerSecondaryNavigationSiteMapTitle
    regions {
      ...RegionFragment
    }
  }

  fragment HomePageFragment on PayloadHomePage {
    title
  }
`;
